import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { Tabs } from "components/navigation";
import { ShowHide } from "components/layout";
import { LicenceCheckingService } from "services";
import { EmployeeLicenceCheckAlerts, EmployeeLicenceCheckEndorsements, EmployeeLicenceCheckEntitlements, EmployeeLicenceCheckHistory, EmployeeLicenceCheckDetails } from "pages/employee/edit/licence-checking/shared";
import axios from "axios";
import classNames from "classnames";
import { PageRestricted } from "pages/errors/page-restricted";
import { UserAccess } from "components/user-access";
import { Modal, ModalSize, NotificationInline, NotificationToaster } from "components/notifications";
import { Button, ButtonSave } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { Intent } from "@blueprintjs/core";

export function EmployeeLicenceChecking({
    employeeId,
    employeeInactive = false,
    employeeName = ''
}) {

    const tabs = [
        { key: "DETAILS", title: "Details" },
        { key: "ENDORSEMENTS", title: "Endorsements" },
        { key: "ENTITLEMENTS", title: "Entitlements" },
        { key: "ALERTS", title: "Alerts" },
        { key: "HISTORY", title: "Check History" }
    ];

    const requiredAction = "LicenceChecking:View";
    const [dataPendingDelete, setDataPendingDelete] = useState(false);
    const [removingData, setRemovingData] = useState(false);
    const [showDeleteLicenceDataConfirm, setShowDeleteLicenceDataConfirm] = useState(false);

    const [deleteDataPhrase, setDeleteDataPhrase] = useState('');

    const requiredDeleteDataPhrase = "confirm delete";

    const [selectedTab, setSelectedTab] = useState("");
    const [tabList, setTabList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    const [latestCheck, setLatestCheck] = useState(null);
    const [latestConsent, setLatestConsent] = useState(null);
    const [latestDetails, setLatestDetails] = useState(null);
    const [hasValidConsent, setHasValidConsent] = useState(false);

    const [licenceCheckOptions, setLicenceCheckOptions] = useState({});

    useEffect(fetchData, [employeeId]);

    function fetchData() {
        if (employeeId != null) {
            setLoadingData(true);
            axios.all([LicenceCheckingService.getLatestLicenceCheck(employeeId), LicenceCheckingService.getLicenceCheckingOptions()])
                .then(([checkResponse, optionsResponse]) => {
                    setLatestCheck(checkResponse?.latestCheck);

                    setLatestConsent(checkResponse?.latestConsent);
                    setLatestDetails({
                        lastCheck: checkResponse?.lastCheck,
                        nextCheck: checkResponse?.nextCheck,
                        policy: checkResponse?.policy,
                        consentExpiry: checkResponse?.consentExpiry,
                        licenceNumber: checkResponse?.licenceNumber,
                        email: checkResponse?.employeeEmail,
                        invalidLicence: checkResponse?.invalidLicence,
                        canCopyLicenceNumber: checkResponse?.canCopyLicenceNumber,
                    })
                    setLicenceCheckOptions(optionsResponse);
                    setHasValidConsent(checkResponse?.hasValidConsent === true);
                    setSelectedTab(tabs[0].key);
                }, function () {

                }).finally(() => {
                    setLoadingData(false);
                });
        }

    }

    useEffect(() => {

        setTabList(tabs.map((tab) => {
            return {
                ...tab,
                disabled: tab.key !== selectedTab && latestCheck == null,
                selected: tab.key === selectedTab,
            }
        }));

    }, [selectedTab, latestCheck]);

    useEffect(() => {

        setDataPendingDelete(latestCheck != null && employeeInactive);

    }, [latestCheck, employeeInactive])

    function onTabClick(tabIndex) {
        setSelectedTab(tabs[tabIndex].key);
    }

    function onDeleteCancel() {
        setShowDeleteLicenceDataConfirm(false);
    }

    function onDeleteShow() {
        setShowDeleteLicenceDataConfirm(true);
    }

    function onDeleteConfirm() {

        setRemovingData(true);

        LicenceCheckingService.deleteLicenceCheckingData(employeeId).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Licence check data has been deleted successfully.");
            setShowDeleteLicenceDataConfirm(false);
            fetchData();
        }, function () {
            NotificationToaster.show(Intent.DANGER, "Licence check data could not be deleted, please try again.");
        }).finally(() => {
            setRemovingData(false);
        });
    }

    function onDeleteDataPhraseChange(event) {
        setDeleteDataPhrase(event.target.value);
    }

    return (

        <div className="row">

            <UserAccess perform={requiredAction}
                yes={() => (
                    <>
                        <h2>Licence Checking</h2>
                        <div className={classNames("spacer-bottom", { "bp6-skeleton": loadingData })}>
                            <p>In this section you can view the licence information for the employee</p>
                        </div>
                        <NotificationInline intent="warning" show={dataPendingDelete} allowClose text="This employee has been set to inactive, so their data will be removed shortly. If you want to keep their licence checking results, please set them back to active." />
                        <div className="spacer-bottom">
                            <Tabs tabs={tabList} singleRow onClick={onTabClick} loading={loadingData} />
                        </div>

                        <ShowHide
                            evaluator={selectedTab === 'DETAILS'}
                            show={(
                                <>
                                    <EmployeeLicenceCheckDetails
                                        latestConsent={latestConsent}
                                        latestCheck={latestCheck}
                                        checkDetails={latestDetails}
                                        validConsent={hasValidConsent}
                                        isLatestCheck
                                        employeeName={employeeName}
                                        employeeId={employeeId}
                                        onReloadData={fetchData}
                                    />
                                    <ShowHide
                                        evaluator={dataPendingDelete}
                                        show={(
                                            <Button text="Remove Employee's Licence Data" intent="danger" onClick={onDeleteShow} />
                                        )}
                                    />
                                </>
                            )}
                        />

                        <ShowHide
                            evaluator={selectedTab === 'ENDORSEMENTS'}
                            show={(
                                <EmployeeLicenceCheckEndorsements
                                    endorsements={latestCheck?.endorsements ?? []}
                                    checkDate={latestCheck?.checkDate}
                                    loading={loadingData}
                                />
                            )}
                        />

                        <ShowHide
                            evaluator={selectedTab === 'ENTITLEMENTS'}
                            show={(
                                <EmployeeLicenceCheckEntitlements
                                    entitlements={latestCheck?.entitlements ?? []}
                                    checkDate={latestCheck?.checkDate}
                                    options={licenceCheckOptions?.entitlements ?? []}
                                />
                            )}
                        />

                        <ShowHide
                            evaluator={selectedTab === 'ALERTS'}
                            show={(
                                <EmployeeLicenceCheckAlerts
                                    employeeId={employeeId}
                                />
                            )}
                        />

                        <ShowHide
                            evaluator={selectedTab === 'HISTORY'}
                            show={(
                                <EmployeeLicenceCheckHistory
                                    employeeId={employeeId}
                                    licenceCheckOptions={licenceCheckOptions}
                                />
                            )}
                        />

                        <Modal
                            isOpen={showDeleteLicenceDataConfirm}
                            clickOutsideClose={false}
                            showCloseButton={false}
                            title={'Delete Licence Check Data?'}
                            onClose={onDeleteCancel}
                            size={ModalSize.SMALL}
                        >

                            <div className="bp6-dialog-body">
                                <p><strong>Removing all data is irreversable.</strong> If you do need to re-check this employee after you delete the data, you will need to send a new declaration to the employee.</p>
                                <p>Enter the phase 'confirm delete' to delete all the licence check data and invalidate the declaration:</p>
                                <FormTextInput
                                    value={deleteDataPhrase}
                                    onChange={onDeleteDataPhraseChange}
                                    disabled={removingData}
                                />
                            </div>

                            <div className="bp6-dialog-footer">

                                <div className="bp6-dialog-footer-actions">
                                    <Button
                                        text="Cancel"
                                        intent="none"
                                        disabled={removingData}
                                        onClick={onDeleteCancel}
                                    ></Button>
                                    <ButtonSave
                                        id="lc-employee-delete"
                                        onClick={onDeleteConfirm}
                                        intent="danger"
                                        icon="trash"
                                        simpleDisabled={deleteDataPhrase !== requiredDeleteDataPhrase}
                                        disabled={removingData}
                                        text={'Delete'}
                                    ></ButtonSave>
                                </div>
                            </div>


                        </Modal>
                    </>
                )}
                no={() => (
                    <PageRestricted />
                )}
            />

        </div>
    );
}

EmployeeLicenceChecking.propTypes = {
    employeeId: PropTypes.string.isRequired,
    employeeInactive: PropTypes.bool,
    employeeName: PropTypes.string,
};